import React, { PropsWithChildren, useCallback, useEffect, useState, MouseEvent } from "react";
import { FocusScope } from "react-aria";
import { Segment, Sidebar } from "semantic-ui-react";
import { useDebounce } from "../../hooks/useDebounce";
import { useToasts } from "react-toast-notifications";
import classNames from "classnames";

export function Flyout({
  children,
  open,
  onClose = () => {},
  disableClose = false,
  contain = true,
  restoreFocus = true,
  autoFocus = true,
  size = "small",
}: PropsWithChildren<{
  open?: boolean;
  onClose?: () => void;
  disableClose?: boolean;
  contain?: boolean;
  restoreFocus?: boolean;
  autoFocus?: boolean;
  size?: "small" | "medium" | "large";
}>) {
  const toast = useToasts();

  const internalOnClose = (ev?: MouseEvent) => {
    if (disableClose) return;

    /**
     * Some of our libraries use portals which can cause issues with the "click outside to close" functionality,
     * because the portal will be outside of the main react app (somewhere else in the DOM).
     *
     * I think its safe to NOT close the flyout if the click is outside the root of the react app, because in theory,
     * if somebody is clicking outside the root of the react app, they're probably not trying to close the flyout?
     */
    const root = document.getElementById("root");
    if (root && ev && !root.contains(ev.target as Node)) {
      return;
    }
    onClose();
  };

  const handleEscape = useCallback((event) => {
    if (event.key === "Escape") {
      internalOnClose();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleEscape, false);

    return () => {
      document.removeEventListener("keydown", handleEscape, false);
    };
  }, [handleEscape]);

  useEffect(() => {
    if (open) toast.removeAllToasts();
  }, [open]);

  return (
    <FocusScope contain={contain} restoreFocus={restoreFocus} autoFocus={autoFocus}>
      <Sidebar
        animation='overlay'
        direction='right'
        vertical='true'
        visible={open}
        onHide={internalOnClose}
        // The sidebar is 2.5rem wider than the segment to help prevent accidental clicks outside the sidebar
        className='tw-z-[2000] tw-flex tw-w-full tw-justify-end tw-shadow-none tw-duration-300 sm:tw-w-fit sm:tw-pl-[2.5rem]'
      >
        <Segment
          className={classNames({
            "tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-none tw-border-y-0 tw-border-r-0 tw-p-0 tw-drop-shadow-lg": true,
            "sm:tw-w-[36rem]": size === "small",
            "sm:tw-w-[48rem]": size === "medium",
            "sm:tw-w-[58rem]": size === "large",
          })}
        >
          {children}
        </Segment>
      </Sidebar>
    </FocusScope>
  );
}

Flyout.Header = ({ children, className = "" }: PropsWithChildren<{ className?: string }>) => {
  return (
    <Segment
      className={`tw-my-0 tw-flex tw-min-h-[4.5rem] tw-items-center tw-justify-between tw-rounded-none tw-border-x-0 tw-border-t-0 ${className}`}
    >
      {children}
    </Segment>
  );
};

Flyout.Body = ({
  children,
  isLoading,
  className = "",
}: PropsWithChildren<{ isLoading?: boolean; className?: string }>) => {
  return (
    <Segment loading={isLoading} basic className={`tw-my-0 tw-h-full tw-w-full tw-overflow-y-auto ${className}`}>
      {children}
    </Segment>
  );
};

Flyout.Footer = ({ children, className = "" }: PropsWithChildren<{ className?: string }>) => {
  return (
    <Segment
      className={`tw-my-0 tw-min-h-[4.5rem] tw-w-full tw-rounded-none tw-border-x-0 tw-border-b-0 tw-bg-white-background ${className}`}
    >
      {children}
    </Segment>
  );
};

Flyout.Banner = ({
  children,
  danger = false,
  warn = false,
  className = "",
}: PropsWithChildren<{ danger?: boolean; warn?: boolean; className?: string }>) => {
  return (
    <Segment
      className={classNames("tw-m-0 tw-rounded-none", className, {
        "tw-border-x-0 tw-border-t-0": !danger && !warn,
        "tw-border-danger-dark tw-bg-danger tw-text-danger-dark": danger,
        "tw-border-warning-dark tw-bg-warning tw-text-warning-dark": warn,
      })}
    >
      {children}
    </Segment>
  );
};

export function useFlyout({
  onOpen = () => {},
  onClose = () => {},
}: { onOpen?: () => void; onClose?: () => void } = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const [id, setId] = useState<number | null>(null);

  // If the user clicks on another record while the flyout is open, triggering the "click outside" onClose event,
  // we want to ignore the onClose event and instead update the flyout with the new record's data.
  const debouncedId = useDebounce(id, 100);
  const didSwap = debouncedId !== id;

  const close = useCallback(() => {
    if (didSwap) return;
    setIsOpen(false);
    setId(null);
    onClose();
  }, [didSwap, setIsOpen, setId]);

  const open = useCallback(() => {
    setIsOpen(true);
    onOpen();
  }, [setIsOpen]);

  const openWithId = useCallback(
    (id: number) => {
      setId(id);
      open();
      onOpen();
    },
    [setId, open],
  );

  return { isOpen, id, open, openWithId, close };
}
